import { useState } from 'react';
import { CheckCircle, Zap } from 'lucide-react';
import { ActiveWidget, participantService } from '../../../services/participant.service';

interface Props {
  sessionId: string;
  widget: ActiveWidget | null;
  onSubmit: () => void;
}

export function ActivityTab({ sessionId, widget, onSubmit }: Props) {
  const [selected, setSelected] = useState<string | null>(widget?.submittedAnswer ?? null);
  const [freeText, setFreeText] = useState('');
  const [ratingValue, setRatingValue] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(!!widget?.submittedAnswer);
  const [error, setError] = useState<string | null>(null);

  if (!widget) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-[var(--pt-surface)] rounded-2xl mx-auto mb-4 flex items-center justify-center">
            <Zap size={24} className="text-[var(--pt-text-dimmer)]" />
          </div>
          <p className="text-[var(--pt-text-dim)] font-medium mb-1">No active activity</p>
          <p className="text-[var(--pt-text-dimmer)] text-sm">Your facilitator will launch an activity here</p>
        </div>
      </div>
    );
  }

  async function handleSubmit() {
    if (submitting) return;
    let answer: string;
    if (widget!.type === 'mcq') {
      if (!selected) return;
      answer = selected;
    } else if (widget!.type === 'free_text') {
      if (!freeText.trim()) return;
      answer = freeText.trim();
    } else {
      if (!ratingValue) return;
      answer = String(ratingValue);
    }

    setSubmitting(true);
    setError(null);
    try {
      await participantService.submitWidgetResponse(sessionId, widget!.widgetId, answer);
      setSubmitted(true);
      onSubmit();
    } catch (err: any) {
      if (err.status === 409) {
        setSubmitted(true);
      } else {
        setError(err.body?.error || err.message);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-primary/20 rounded-2xl mx-auto mb-4 flex items-center justify-center">
            <CheckCircle size={28} className="text-primary" />
          </div>
          <p className="text-[var(--pt-text)] font-semibold mb-1">Response recorded</p>
          {selected && <p className="text-[var(--pt-text-dim)] text-sm">You answered: <span className="text-primary">{selected}</span></p>}
        </div>
      </div>
    );
  }

  let widgetTypeLabel: string;
  if (widget.type === 'mcq') widgetTypeLabel = 'Multiple choice';
  else if (widget.type === 'free_text') widgetTypeLabel = 'Open question';
  else widgetTypeLabel = 'Rating';

  return (
    <div className="flex-1 overflow-y-auto p-4">
      <div className="mb-6">
        <span className="text-xs text-primary font-medium uppercase tracking-wide">
          {widgetTypeLabel}
        </span>
        <h2 className="text-[var(--pt-text)] font-semibold text-xl mt-2 leading-snug">{widget.question}</h2>
      </div>

      {widget.type === 'mcq' && widget.options && (
        <div className="space-y-3">
          {widget.options.map(option => (
            <button
              key={option}
              onClick={() => setSelected(option)}
              className={`w-full text-left px-4 py-4 rounded-2xl border text-sm font-medium transition-all active:scale-98 ${
                selected === option
                  ? 'bg-primary border-primary text-navy'
                  : 'bg-[var(--pt-surface)] border-[var(--pt-border)] text-[var(--pt-text)] hover:border-primary/50'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      )}

      {widget.type === 'free_text' && (
        <textarea
          value={freeText}
          onChange={e => setFreeText(e.target.value)}
          placeholder="Type your answer…"
          rows={5}
          className="w-full bg-[var(--pt-surface-strong)] border border-[var(--pt-border-strong)] rounded-xl px-4 py-3 text-[var(--pt-text)] placeholder-[var(--pt-text-dimmer)] text-sm focus:outline-none focus:border-primary transition-all resize-none"
          maxLength={1000}
        />
      )}

      {widget.type === 'rating' && (
        <div className="flex gap-3 justify-center flex-wrap">
          {Array.from({ length: widget.ratingMax || 5 }, (_, i) => i + 1).map(n => (
            <button
              key={n}
              onClick={() => setRatingValue(n)}
              className={`w-12 h-12 rounded-xl text-base font-bold transition-all active:scale-90 ${
                ratingValue === n
                  ? 'bg-primary text-navy'
                  : 'bg-[var(--pt-surface-strong)] text-[var(--pt-text)] hover:bg-[var(--pt-surface-strong)]'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      )}

      {error && <p className="mt-4 text-red-400 text-sm bg-red-400/10 rounded-lg px-3 py-2">{error}</p>}

      <button
        onClick={handleSubmit}
        disabled={
          submitting ||
          (widget.type === 'mcq' && !selected) ||
          (widget.type === 'free_text' && !freeText.trim()) ||
          (widget.type === 'rating' && !ratingValue)
        }
        className="mt-6 w-full bg-primary text-navy font-semibold py-3.5 rounded-xl text-base disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition-all"
      >
        {submitting ? 'Submitting…' : 'Submit'}
      </button>
    </div>
  );
}
